import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { compareTunnelAddress } from './addressing';

/**
 * Which section the board selects once the one the user deleted is gone.
 *
 * Two candidate lists, tried in order:
 *
 * 1. **Inside the deleted section's own tunnel**, by `order_rate` — the legacy
 *    pick (`section.toolbar.js` `delete_section`: `order - 2` of the list,
 *    reversed for a right-to-left tunnel, falling back to `order`). Upstream
 *    neighbour first, downstream if the deleted section was the upstream end.
 *
 * 2. **The whole AHU, in position order** (`row:level:position` then
 *    `order_rate` — the Data Bar / tunnel-pill order, `addressing.ts`), for a
 *    hybrid AHU only. Hybrid needs it because every "Add Section" there makes a
 *    whole new tunnel holding one section (`hybridInsertionBindings.ts`), so
 *    list 1 is empty for all but a handful of hybrid deletes and the board used
 *    to deselect entirely instead of moving to the neighbouring position.
 *
 *    Non-hybrid is deliberately left at list 1: its tunnels carry no
 *    row:level:position addressing, so an AHU-wide order would be arbitrary.
 *
 * Both lists walk outwards until they find a section that is actually staying,
 * skipping `alsoDeletedIds` — the bound dual-tunnel peer the server cascades
 * with the section it sits on, which lives in another tunnel and so is a
 * candidate in list 2.
 */
export function pickSectionAfterDelete(args: {
  /** Every section of the AHU, as the board holds them pre-delete. */
  sections: IAhuBoardSection[];
  tunnels: IAhuBoardTunnel[];
  deleted: IAhuBoardSection;
  /** Ids the server deletes along with it (the `sitting_on_id` peer). */
  alsoDeletedIds?: Array<number | undefined>;
  isHybrid?: boolean;
}): IAhuBoardSection | null {
  const { sections, tunnels, deleted, alsoDeletedIds = [], isHybrid } = args;
  const doomed = new Set<number>([deleted.id]);
  alsoDeletedIds.forEach((id) => id != null && doomed.add(id));

  const byOrderRate = (a: IAhuBoardSection, b: IAhuBoardSection) =>
    (a.order_rate ?? 0) - (b.order_rate ?? 0);

  const tunnelSections = sections
    .filter((s) => s.tunnel_id === deleted.tunnel_id)
    .sort(byOrderRate);
  const tunnel = tunnels.find((t) => t.id === deleted.tunnel_id);
  // Right-to-left tunnel: `order_rate` runs against the screen, so the
  // upstream neighbour is the next index, not the previous one.
  const reversed = tunnel?.airflow_direction_id === 2;
  const upstream: 1 | -1 = reversed ? 1 : -1;
  const downstream: 1 | -1 = reversed ? -1 : 1;
  const inTunnel =
    walk(tunnelSections, deleted, upstream, doomed) ??
    walk(tunnelSections, deleted, downstream, doomed);
  if (inTunnel) return inTunnel;

  if (!isHybrid) return null;

  const tunnelById = new Map(tunnels.map((t) => [t.id, t]));
  const ordered = [...sections].sort((a, b) => {
    const ta = tunnelById.get(a.tunnel_id);
    const tb = tunnelById.get(b.tunnel_id);
    if (ta && tb) {
      const byAddress = compareTunnelAddress(ta, tb);
      if (byAddress) return byAddress;
    }
    return byOrderRate(a, b) || a.id - b.id;
  });
  // Previous position, else the next one.
  return walk(ordered, deleted, -1, doomed) ?? walk(ordered, deleted, 1, doomed);
}

/**
 * First section `step`-wards of `from` that this delete leaves standing, or
 * null at the end of the list.
 */
function walk(
  list: IAhuBoardSection[],
  from: IAhuBoardSection,
  step: 1 | -1,
  doomed: Set<number>
): IAhuBoardSection | null {
  const at = list.findIndex((s) => s.id === from.id);
  if (at === -1) return null;
  for (let i = at + step; i >= 0 && i < list.length; i += step) {
    if (!doomed.has(list[i].id)) return list[i];
  }
  return null;
}
